/**
 * Local persistence for one-time conversion prompts:
 *  - welcome modal for newly registered users
 *  - plan activation / plan change celebration modals
 *
 * Keys are namespaced per user id so shared browsers don't suppress
 * each other's prompts. All storage access is fail-safe: private
 * browsing or disabled storage simply degrades to "always eligible".
 */

const PREFIX = "dv.conversion.";

// A signup older than this is no longer greeted (the moment has passed).
const WELCOME_WINDOW_DAYS = 14;

// How long a checkout "thank you" suppresses the duplicate celebration
// that would otherwise fire when the webhook flips the tier to paid.
const ACTIVATION_SUPPRESS_MS = 6 * 60 * 60 * 1000;

const storageKey = (kind, userId) => `${PREFIX}${kind}.${userId}`;

function safeGet(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    return null;
  }
}

function safeSet(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    // storage unavailable — prompts may repeat, which is acceptable
  }
}

function safeRemove(key) {
  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    // ignore
  }
}

export function isRecentSignup(user) {
  if (!user || !user.date_joined) return false;
  const joined = new Date(user.date_joined).getTime();
  if (Number.isNaN(joined)) return false;
  const ageMs = Date.now() - joined;
  return ageMs <= WELCOME_WINDOW_DAYS * 24 * 60 * 60 * 1000;
}

export function hasSeenWelcome(userId) {
  return safeGet(storageKey("welcome", userId)) === "1";
}

export function markWelcomeSeen(userId) {
  safeSet(storageKey("welcome", userId), "1");
}

export function getLastKnownTier(userId) {
  return safeGet(storageKey("tier", userId));
}

export function setLastKnownTier(userId, tier) {
  safeSet(storageKey("tier", userId), tier);
}

export function markActivationCelebrated(userId) {
  safeSet(storageKey("celebrated", userId), String(Date.now()));
}

/**
 * Returns true (and clears the flag) if a checkout thank-you was shown
 * recently, so the tier-transition watcher doesn't celebrate twice.
 */
export function consumeActivationCelebrated(userId) {
  const key = storageKey("celebrated", userId);
  const raw = safeGet(key);
  if (!raw) return false;
  safeRemove(key);
  const timestamp = Number(raw);
  if (Number.isNaN(timestamp)) return false;
  return Date.now() - timestamp <= ACTIVATION_SUPPRESS_MS;
}
